import { routeNames } from "@/routes";
import { useEntityAuditStore } from "@modernary/mui/components/audit";
import { getLookupDescription } from "@modernary/mui/components/lookup";
import { useEntityNoteStore } from "@modernary/mui/components/note";
import type { EntityPreview } from "@modernary/mui/components/read";
import { useApi } from "@modernary/mui/utils/api";
import { AuthRequest, authorizationClient, withAuthorization } from "@modernary/mui/utils/authorization";
import { generateComputedResource } from "@modernary/mui/utils/helpers";
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";
import type { Distributor } from "../distributor/distributor";
import { getReportPayments } from "../reportPayment/reportPaymentApi";
import type { DistributorReport } from "./distributorReport";
import { getDistributorReportById } from "./distributorReportApi";
import distributorReportService from "./distributorReportService";

export const useDistributorReportStore = defineStore("distributorReportStore", () => {
  const entityAuditStore = useEntityAuditStore();
  const entityNoteStore = useEntityNoteStore();

  // state
  const distributorReport = ref<DistributorReport | null>(null);
  const distributor = ref<Distributor | null>(null);
  const entityPreviews = ref<Promise<EntityPreview>[]>([]);
  const reportPaymentsCount = ref(0);
  /** null while ReadAnnotation authorization is pending */
  const canViewNotes = ref<boolean | null>(null);

  // getters
  const authResource = generateComputedResource(distributorReport, computed);

  // actions
  function fill(entity: DistributorReport) {
    distributorReport.value = entity;
    distributor.value = entity.distributor;

    entityAuditStore.fill(entity, {
      entityType: "distributorReport",
      entityId: entity.id,
      statusLookupGroupCode: "ReportStatus",
    });
    entityNoteStore.fill(entity);
    entityPreviews.value = [];

    if (distributor.value) {
      entityPreviews.value.push(
        getDistributorEntityPreview(distributor.value, entity.distributorId, routeNames.distributor.distributorReports),
      );
    }
  }

  function reset() {
    distributorReport.value = null;
    distributor.value = null;
    reportPaymentsCount.value = 0;
    canViewNotes.value = null;

    entityPreviews.value = [];
    entityAuditStore.reset();
  }

  const getDistributorEntityPreview = async (distributor: Distributor, disId: number, viewAllRouteName?: string) => {
    let freq = distributor.reportFrequencyMurn ? await getLookupDescription(distributor.reportFrequencyMurn) : "";

    const result: EntityPreview = {
      label: distributor.name,
      title: freq ?? "",
      to: { name: routeNames.distributor.view, params: { id: disId } },
      parentEntityLabel: "Distributor",
    };
    if (viewAllRouteName) {
      result.viewAllTo = { name: viewAllRouteName, params: { id: disId } };
    }
    return result;
  };

  const { exec: fetchDistributorReport } = withAuthorization(
    useApi<DistributorReport>(getDistributorReportById, {
      responseAdapter: async (response) => {
        return await distributorReportService.responseAdapter(response);
      },
    }),
  );

  async function fetchAndFillDistributorReport(distributorReportId: string | number) {
    await fetchDistributorReport(distributorReportId).then((dr) => {
      dr && fill(dr);
    });
  }

  async function fetchNotesAuthorization(distributorReportId: string | number) {
    canViewNotes.value = null;

    const viewNotesAR = new AuthRequest();
    viewNotesAR.permissionName = "ReadAnnotation";
    viewNotesAR.resource = { reportId: distributorReportId };

    const [authorized] = await authorizationClient.isAuthorizedArray([viewNotesAR]);
    canViewNotes.value = authorized;
  }

  return {
    // state
    distributorReport,
    distributor,
    entityPreviews,
    reportPaymentsCount,
    canViewNotes,
    // getters
    authResource,
    // actions
    fill,
    reset,
    fetchAndFillDistributorReport,
    fetchNotesAuthorization,
  };
});
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDistributorReportStore, import.meta.hot));
}
